import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { Save, RefreshCw, Mail, Send, Wallet, Webhook, Upload, MessageSquare, CheckCircle2, XCircle, Loader2, Phone, Package, ShieldOff, Users2, Eye, AlertTriangle, SkipForward, Lock, FileText } from "lucide-react";

const JOB_LEVELS = ["Hafif İş", "Orta İş", "Ağır İş", "Kaporta/Boya", "Elektrik", "Diğer"];
const DELAY_KEYS = ["Gecikme Yok", "Parça Bekleme", "Arıza Tespiti Uzadı", "Diğer"];

export default function Settings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [netgsmStatus, setNetgsmStatus] = useState(null);

  const load = async () => setS((await api.get("/settings")).data);
  const loadNetgsmStatus = async () => {
    try { const { data } = await api.get("/settings/netgsm-status"); setNetgsmStatus(data); } catch {}
  };
  useEffect(() => { load(); loadNetgsmStatus(); }, []);

  if (!s) return null;

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        points: s.points,
        partnership_primary: parseFloat(s.partnership_primary),
        partnership_secondary: parseFloat(s.partnership_secondary),
        repeat_penalty: parseInt(s.repeat_penalty),
        tl_multiplier: parseFloat(s.tl_multiplier),
        repeat_window_days: parseInt(s.repeat_window_days),
        delay_multipliers: Object.fromEntries(
          Object.entries(s.delay_multipliers).map(([k, v]) => [k, parseFloat(v)])
        ),
        notification_email: s.notification_email || "",
        critical_repeat_threshold: parseInt(s.critical_repeat_threshold) || 3,
        bonus_mode: s.bonus_mode || "pool",
        monthly_pool_budget: parseFloat(s.monthly_pool_budget) || 50000,
        pool_penalty_per_incident: parseFloat(s.pool_penalty_per_incident) || 1250,
        pool_rule_start_month: (s.pool_rule_start_month || "2026-09").trim(),
        main_system_webhook_url: (s.main_system_webhook_url || "").trim(),
        main_system_webhook_token: (s.main_system_webhook_token || "").trim(),
        netgsm_usercode: (s.netgsm_usercode || "").trim(),
        netgsm_password: s.netgsm_password || "",
        netgsm_msgheader: (s.netgsm_msgheader || "").trim(),
        otp_ttl_minutes: parseInt(s.otp_ttl_minutes) || 3,
        otp_resend_cooldown_sec: parseInt(s.otp_resend_cooldown_sec) || 60,
        sms_auth_enabled: s.sms_auth_enabled !== false,
        otp_master_code: (s.otp_master_code || "").trim(),
        driver_bonus_base_tl: parseFloat(s.driver_bonus_base_tl) || 5000,
        driver_weekly_days_start_month: (s.driver_weekly_days_start_month || "2026-10").trim(),
        driver_daily_extra_tl: (s.driver_daily_extra_tl === "" || s.driver_daily_extra_tl == null)
          ? null : parseFloat(s.driver_daily_extra_tl),
        field_bonus_base_tl: parseFloat(s.field_bonus_base_tl) || 3000,
        field_supervisor_count: parseInt(s.field_supervisor_count) || 2,
        field_bonus_tiers: Array.isArray(s.field_bonus_tiers) ? s.field_bonus_tiers.map((t) => ({
          tier: parseInt(t.tier) || 0,
          label: (t.label || "").toString(),
          condition_label: (t.condition_label || "").toString(),
          max_growth_pct: t.max_growth_pct === "" || t.max_growth_pct == null ? null : parseFloat(t.max_growth_pct),
          max_inclusive: t.max_inclusive !== false,
          bonus_tl: parseFloat(t.bonus_tl) || 0,
        })) : undefined,
      };
      await api.put("/settings", payload);
      toast.success("Ayarlar güncellendi");
    } catch { toast.error("Kayıt hatası"); }
    finally { setBusy(false); }
  };

  const testEmail = async () => {
    try {
      const { data } = await api.post("/notifications/test");
      if (data.sent) toast.success(`Test e-postası gönderildi: ${s.notification_email}`);
      else toast.error("E-posta gönderilemedi");
    } catch { toast.error("E-posta gönderilemedi"); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-mono">Yapılandırma</div>
          <h1 className="mt-1 font-heading font-bold text-3xl lg:text-4xl tracking-tight text-slate-900">Prim & Puanlama Ayarları</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline"><RefreshCw className="w-4 h-4 mr-2" /> Yenile</Button>
          <Button onClick={save} disabled={busy} data-testid="save-settings-btn" className="bg-[#1E3A8A] hover:bg-[#1e40af]">
            <Save className="w-4 h-4 mr-2" /> {busy ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>
      </div>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100"><CardTitle className="font-heading text-lg flex items-center gap-2"><Mail className="w-4 h-4 text-[#1E3A8A]" /> Bildirim & Otomasyon</CardTitle></CardHeader>
        <CardContent className="pt-6">
          <p className="text-xs text-slate-500 mb-4">
            Kritik araç uyarıları ve aylık prim raporları bu e-posta adresine gönderilir.
            Aynı araç için 24 saat içinde tekrar bildirim gönderilmez.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider">Bildirim E-posta Adresi</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="email"
                  value={s.notification_email || ""}
                  onChange={(e) => setS({ ...s, notification_email: e.target.value })}
                  placeholder="patron@sirket.com"
                  data-testid="notification-email-input"
                  className="flex-1"
                />
                <Button type="button" variant="outline" onClick={testEmail} data-testid="test-email-btn" className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-blue-50">
                  <Send className="w-4 h-4 mr-2" /> Test Gönder
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Aylık rapor her ayın 1'i saat 09:00'da otomatik gönderilir.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Kritik Araç Eşiği</Label>
              <Input
                type="number" min="1" className="mt-1"
                value={s.critical_repeat_threshold ?? 3}
                onChange={(e) => setS({ ...s, critical_repeat_threshold: e.target.value })}
                data-testid="critical-threshold-input"
              />
              <p className="text-xs text-slate-500 mt-1">Bu sayıda tekrar arıza olursa uyarı</p>
            </div>
          </div>
        </CardContent>
      </Card>


      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100"><CardTitle className="font-heading text-lg flex items-center gap-2"><Users2 className="w-4 h-4 text-emerald-700" /> Şoför Prim — Haftalık Gün Girişi</CardTitle></CardHeader>
        <CardContent className="pt-6">
          <p className="text-xs text-slate-500 mb-4">
            Bu ay ve sonrasında şoföre w1..w4 girildiğinde yeni formül uygulanır (taban + ek). Daha öncesi eski gün bazlı orantı formülü aynen devam eder.
            Günlük ek boşsa taban/30 kullanılır.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Haftalık Gün Girişi Başlangıç Ayı</Label>
              <Input
                type="month"
                className="mt-1 font-mono"
                value={s.driver_weekly_days_start_month || "2026-10"}
                onChange={(e) => setS({ ...s, driver_weekly_days_start_month: e.target.value })}
                data-testid="driver-weekly-start-month"
              />
              <p className="text-[11px] text-slate-500 mt-1">Varsayılan 2026-10. Bu aydan önceki aylar değişmez.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Günlük Ek (TL/gün)</Label>
              <Input
                type="number" step="0.01" min="0"
                className="mt-1 font-mono"
                placeholder={`Boş = ${Math.round(((s.driver_bonus_base_tl || 5000)/30)*100)/100}`}
                value={s.driver_daily_extra_tl ?? ""}
                onChange={(e) => setS({ ...s, driver_daily_extra_tl: e.target.value })}
                data-testid="driver-daily-extra"
              />
              <p className="text-[11px] text-slate-500 mt-1">Boş bırakılırsa taban/30 ({((s.driver_bonus_base_tl || 5000)/30).toFixed(2)} ₺/gün).</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100"><CardTitle className="font-heading text-lg flex items-center gap-2"><Upload className="w-4 h-4 text-emerald-700" /> Toplu JSON İçe Aktar (Bulk Import)</CardTitle></CardHeader>
        <CardContent className="pt-6">
          <BulkImportPanel />
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100"><CardTitle className="font-heading text-lg flex items-center gap-2"><Upload className="w-4 h-4 text-indigo-700" /> Toplu Vardiya JSON İçe Aktar</CardTitle></CardHeader>
        <CardContent className="pt-6">
          <BulkShiftImportPanel />
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100"><CardTitle className="font-heading text-lg flex items-center gap-2"><Webhook className="w-4 h-4 text-[#1E3A8A]" /> Webhook Gönderim Geçmişi</CardTitle></CardHeader>
        <CardContent className="pt-6">
          <WebhookHistoryPanel />
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100"><CardTitle className="font-heading text-lg flex items-center gap-2"><Webhook className="w-4 h-4 text-[#1E3A8A]" /> Ana Sistem Webhook (Bursa Şubesi)</CardTitle></CardHeader>
        <CardContent className="pt-6">
          <p className="text-xs text-slate-500 mb-4">
            Her yeni iş emri kaydında arka planda ana yönetim sistemine bir POST isteği gönderilir. Hata durumunda prim kaydı engellenmez, sadece log'a düşer.
            URL boş bırakılırsa gönderim atlanır.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider">Webhook URL</Label>
              <Input
                type="url"
                value={s.main_system_webhook_url || ""}
                onChange={(e) => setS({ ...s, main_system_webhook_url: e.target.value })}
                placeholder="https://ana-sistem.example.com/api/is-emri-webhook"
                data-testid="webhook-url-input"
                className="mt-1 font-mono text-xs"
              />
              <p className="text-xs text-slate-500 mt-1">
                Gönderilen alanlar: <span className="font-mono">sube · plaka · tarih · yapilan_is · sorumlu_usta · is_seviyesi · maliyet · kaynak</span>
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">X-Webhook-Key Değeri</Label>
              <Input
                type="password"
                value={s.main_system_webhook_token || ""}
                onChange={(e) => setS({ ...s, main_system_webhook_token: e.target.value })}
                placeholder="••••••••••••"
                data-testid="webhook-token-input"
                className="mt-1 font-mono text-xs"
                autoComplete="new-password"
              />
              <p className="text-xs text-slate-500 mt-1">
                Header: <code>X-Webhook-Key: …</code>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>


      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100"><CardTitle className="font-heading text-lg flex items-center gap-2"><MessageSquare className="w-4 h-4 text-emerald-700" /> SMS OTP (Netgsm)</CardTitle></CardHeader>
        <CardContent className="pt-6">
          {netgsmStatus?.last_result && (
            <div
              className={`mb-4 rounded-lg border p-3 flex items-start gap-3 ${
                netgsmStatus.last_result.ok
                  ? "border-emerald-300 bg-emerald-50"
                  : "border-red-300 bg-red-50"
              }`}
              data-testid="netgsm-status-card"
            >
              <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${netgsmStatus.last_result.ok ? "bg-emerald-500" : "bg-red-500"} text-white text-xs font-mono font-bold`}>
                {netgsmStatus.last_result.code || "?"}
              </div>
              <div className="flex-1 text-xs">
                <div className={`font-heading font-bold ${netgsmStatus.last_result.ok ? "text-emerald-800" : "text-red-800"}`}>
                  Son SMS Sonucu: {netgsmStatus.last_result.label || "?"}
                </div>
                <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-slate-700">
                  <div>Netgsm: <code className="font-mono">Error Code: {netgsmStatus.last_result.code}</code></div>
                  <div>HTTP: <code className="font-mono">{netgsmStatus.last_result.http_status ?? "—"}</code></div>
                  <div>Alıcı: <code className="font-mono">{netgsmStatus.last_result.phone_masked || "—"}</code></div>
                  <div>Zaman: <code className="font-mono">{netgsmStatus.last_result.at ? new Date(netgsmStatus.last_result.at).toLocaleString("tr-TR") : "—"}</code></div>
                </div>
                {netgsmStatus.last_result.raw && (
                  <div className="mt-1 text-[10px] text-slate-500 break-all">Raw: <code>{netgsmStatus.last_result.raw}</code></div>
                )}
                {!netgsmStatus.last_result.ok && (
                  <div className="mt-2 text-[11px] text-red-700 leading-snug">
                    💡 Bakiye/başlık onayınızı Netgsm panelinden kontrol edin. Şoförler bu arada <strong>123456</strong> veya telefon son 4 hanesi ile giriş yapabilir.
                  </div>
                )}
              </div>
              <button onClick={loadNetgsmStatus} className="text-slate-400 hover:text-slate-700" title="Yenile" data-testid="netgsm-status-refresh"><RefreshCw className="w-4 h-4" /></button>
            </div>
          )}
          <p className="text-xs text-slate-500 mb-4">
            Personel SMS OTP ile giriş yapar. Netgsm bilgileri eksikse <strong>123456</strong> ve telefon son 4 hanesi fail-safe olarak devrededir.
          </p>
          <NetgsmHealthPanel configured_header={s.netgsm_msgheader || ""} onTestSent={loadNetgsmStatus} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Netgsm Kullanıcı Kodu</Label>
              <Input
                type="text"
                value={s.netgsm_usercode || ""}
                onChange={(e) => setS({ ...s, netgsm_usercode: e.target.value.trim() })}
                placeholder="850xxxxxxx"
                data-testid="netgsm-usercode-input"
                className="mt-1 font-mono text-xs"
                autoComplete="off"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Netgsm API Şifresi</Label>
              <Input
                type="password"
                value={s.netgsm_password || ""}
                onChange={(e) => setS({ ...s, netgsm_password: e.target.value })}
                placeholder="••••••••••"
                data-testid="netgsm-password-input"
                className="mt-1 font-mono text-xs"
                autoComplete="new-password"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Gönderici Başlığı</Label>
              <Input
                type="text"
                value={s.netgsm_msgheader || ""}
                onChange={(e) => setS({ ...s, netgsm_msgheader: e.target.value })}
                placeholder="YAZTUR"
                data-testid="netgsm-msgheader-input"
                className="mt-1 font-mono text-xs"
              />
              <p className="text-[10px] text-slate-500 mt-1">Netgsm panelinde onaylı başlık (11 karakter maks).</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Kod Süresi (dk)</Label>
              <Input
                type="number"
                min="1" max="30"
                value={s.otp_ttl_minutes ?? 5}
                onChange={(e) => setS({ ...s, otp_ttl_minutes: parseInt(e.target.value) || 5 })}
                data-testid="otp-ttl-input"
                className="mt-1 font-mono text-xs"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Tekrar Gönderim (sn)</Label>
              <Input
                type="number"
                min="15" max="300"
                value={s.otp_resend_cooldown_sec ?? 60}
                onChange={(e) => setS({ ...s, otp_resend_cooldown_sec: parseInt(e.target.value) || 60 })}
                data-testid="otp-cooldown-input"
                className="mt-1 font-mono text-xs"
              />
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  SMS Girişleri Aktif
                </div>
                <p className="text-xs text-slate-500 mt-0.5">Kapatılırsa /admin-emergency dışında hiçbir giriş çalışmaz.</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer" data-testid="sms-auth-toggle">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={s.sms_auth_enabled !== false}
                  onChange={(e) => setS({ ...s, sms_auth_enabled: e.target.checked })}
                />
                <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-amber-800">🔑 Ek Acil Durum Kodu (opsiyonel)</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={s.otp_master_code || ""}
                  onChange={(e) => setS({ ...s, otp_master_code: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                  placeholder="Örn: 1907"
                  data-testid="otp-master-code-input"
                  className="mt-1 font-mono text-base tracking-widest border-amber-300 focus-visible:ring-amber-400"
                  autoComplete="off"
                />
                <p className="text-[10px] text-amber-700 mt-1">
                  Sabit <code className="bg-amber-100 px-1 rounded">123456</code> ve <strong>telefon son 4 hanesi</strong> her zaman geçerlidir. Buraya ek bir sabit kod tanımlayabilirsiniz (opsiyonel).
                </p>
              </div>
              <div className="p-3 rounded border border-emerald-200 bg-emerald-50 text-xs text-emerald-900 leading-relaxed">
                <strong>🛡️ Fail-Safe Giriş</strong>
                <ul className="mt-1 space-y-1 list-disc list-inside">
                  <li>Netgsm çalışıyorsa 6 haneli dinamik SMS OTP (3 dk geçerli).</li>
                  <li>SMS gitmese bile giriş kilitlenmez — her zaman <strong>123456</strong> veya <strong>telefon son 4 hanesi</strong> ile giriş yapılır.</li>
                  <li>Acil durum kod kullanımı sunucu loglarına audit edilir.</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 rounded border border-amber-200 bg-amber-50 text-xs text-amber-800 leading-relaxed">
            <strong>Not:</strong> SMS OTP çalışması için <em>her</em> kullanıcının Kullanıcılar sayfasında cep telefonu girilmiş olmalı. Cep numarası olmayan personel giriş yapamaz.
          </div>
        </CardContent>
      </Card>

      {/* Update 24 — Hariç tutulan personel (kalıcı silinmiş) */}
      <ExcludedPersonnelPanel />

      {/* Update 29.2 — Saha Sorumlusu Primi (yalnız admin görür — sayfa zaten admin-only) */}
      <Card className="border-slate-200 shadow-none" data-testid="field-bonus-settings-card">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Users2 className="w-4 h-4 text-[#1E3A8A]" /> Saha Sorumlusu Primi
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <p className="text-xs text-slate-500 mb-4">
            Her ay <b>kişi başı</b> hesaplanır. Taban tutar herkese verilir; yolcu büyümesine göre kademeli ek prim eklenir. Şirket toplamı = kişi başı net × saha sorumlusu sayısı. Görev aksaklıkları prime <b>etki etmez</b>. Kilitli aylar bu ayarlardan etkilenmez.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Taban Prim (Kişi Başı, TL)</Label>
              <Input type="number" step="100" min="0" className="mt-1 font-mono"
                value={s.field_bonus_base_tl ?? 3000}
                onChange={(e) => setS({ ...s, field_bonus_base_tl: e.target.value })}
                data-testid="field-base-tl-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Saha Sorumlusu Sayısı</Label>
              <Input type="number" step="1" min="1" className="mt-1 font-mono"
                value={s.field_supervisor_count ?? 2}
                onChange={(e) => setS({ ...s, field_supervisor_count: e.target.value })}
                data-testid="field-supervisor-count-input" />
              <p className="text-xs text-slate-500 mt-1">Şirket toplamı bu sayıyla çarpılır.</p>
            </div>
          </div>

          <Label className="text-xs uppercase tracking-wider block mb-2">Yolcu Artışı Basamakları</Label>
          <div className="border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-2 py-2 w-16">Basamak</th>
                  <th className="text-left px-2 py-2">Etiket / Koşul</th>
                  <th className="text-right px-2 py-2 w-32">Üst Sınır (%)</th>
                  <th className="text-center px-2 py-2 w-20">Dahil</th>
                  <th className="text-right px-2 py-2 w-32">Ek Prim (TL)</th>
                </tr>
              </thead>
              <tbody>
                {(s.field_bonus_tiers || []).map((t, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`field-tier-row-${i}`}>
                    <td className="px-2 py-1 font-mono">{t.tier ?? i + 1}</td>
                    <td className="px-2 py-1">
                      <Input className="h-8 text-xs" value={t.condition_label || ""}
                        onChange={(e) => {
                          const nt = [...s.field_bonus_tiers];
                          nt[i] = { ...nt[i], condition_label: e.target.value };
                          setS({ ...s, field_bonus_tiers: nt });
                        }}
                        data-testid={`field-tier-${i}-condition`} />
                    </td>
                    <td className="px-2 py-1 text-right">
                      {i === (s.field_bonus_tiers.length - 1) ? (
                        <span className="text-slate-400 text-[10px]">açık uç</span>
                      ) : (
                        <Input type="number" step="0.1" className="h-8 text-xs font-mono text-right"
                          value={t.max_growth_pct ?? ""}
                          onChange={(e) => {
                            const nt = [...s.field_bonus_tiers];
                            nt[i] = { ...nt[i], max_growth_pct: e.target.value };
                            setS({ ...s, field_bonus_tiers: nt });
                          }}
                          data-testid={`field-tier-${i}-max`} />
                      )}
                    </td>
                    <td className="px-2 py-1 text-center">
                      <input type="checkbox" checked={t.max_inclusive !== false}
                        onChange={(e) => {
                          const nt = [...s.field_bonus_tiers];
                          nt[i] = { ...nt[i], max_inclusive: e.target.checked };
                          setS({ ...s, field_bonus_tiers: nt });
                        }}
                        data-testid={`field-tier-${i}-inclusive`} />
                    </td>
                    <td className="px-2 py-1 text-right">
                      <Input type="number" step="500" min="0" className="h-8 text-xs font-mono text-right"
                        value={t.bonus_tl ?? 0}
                        onChange={(e) => {
                          const nt = [...s.field_bonus_tiers];
                          nt[i] = { ...nt[i], bonus_tl: e.target.value };
                          setS({ ...s, field_bonus_tiers: nt });
                        }}
                        data-testid={`field-tier-${i}-bonus`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">
            "Dahil" işaretliyse üst sınır değeri o basamağa dahildir (ör. dahil + üst=%1 → tam %1 bu basamaktadır). Sonuncu basamak her zaman açık uçludur.
          </p>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100"><CardTitle className="font-heading text-lg flex items-center gap-2"><Wallet className="w-4 h-4 text-[#1E3A8A]" /> Sabit Prim Havuzu</CardTitle></CardHeader>
        <CardContent className="pt-6">
          <p className="text-xs text-slate-500 mb-4">
            Ustalar aylık toplu havuzdan puanları oranında pay alır. Yolda kalma / ekstra mesai (şoför kusuru YOK) her olayda havuzdan <b>SABİT TL</b> kesinti yapılır (Update 19).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Aylık Havuz Bütçesi (TL)</Label>
              <Input type="number" step="1000" className="mt-1 font-mono" value={s.monthly_pool_budget ?? 50000}
                onChange={(e) => setS({ ...s, monthly_pool_budget: e.target.value })}
                data-testid="pool-budget-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Mesai/Arıza Başına Havuz Kesintisi (TL)</Label>
              <Input type="number" step="50" className="mt-1 font-mono" value={s.pool_penalty_per_incident ?? 1250}
                onChange={(e) => setS({ ...s, pool_penalty_per_incident: e.target.value })}
                data-testid="pool-penalty-input" />
              <p className="text-xs text-slate-500 mt-1">Her mesai/arıza olayında (şoför kusuru YOK) havuzdan bu tutar kadar sabit kesinti yapılır.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Yeni Kural Başlangıç Ayı</Label>
              <Input type="text" placeholder="YYYY-MM" className="mt-1 font-mono" value={s.pool_rule_start_month ?? "2026-09"}
                onChange={(e) => setS({ ...s, pool_rule_start_month: e.target.value })}
                data-testid="pool-rule-start-month" />
              <p className="text-xs text-slate-500 mt-1">Bu ay ve sonrası yeni sabit TL formülüyle hesaplanır. Öncesi eski (bileşik %) formülüyle.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Prim Modu</Label>
              <select
                className="w-full mt-1 h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                value={s.bonus_mode || "pool"}
                onChange={(e) => setS({ ...s, bonus_mode: e.target.value })}
              >
                <option value="pool">Sabit Havuz (Önerilen)</option>
                <option value="multiplier">Legacy TL Çarpanı</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>


      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100"><CardTitle className="font-heading text-lg">İş Seviyesi Puanları</CardTitle></CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {JOB_LEVELS.map((lvl) => (
              <div key={lvl}>
                <Label className="text-xs uppercase tracking-wider">{lvl}</Label>
                <Input
                  type="number"
                  className="mt-1"
                  value={s.points[lvl] ?? 0}
                  onChange={(e) => setS({ ...s, points: { ...s.points, [lvl]: parseInt(e.target.value) || 0 } })}
                  data-testid={`points-${lvl}`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100"><CardTitle className="font-heading text-lg">Gecikme Çarpanları</CardTitle></CardHeader>
        <CardContent className="pt-6">
          <p className="text-xs text-slate-500 mb-4">
            "Parça Bekleme" gibi dış nedenlerde ustaların puanı düşürülmez (1.0). Değerler 0 ile 1 arasında olmalıdır.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {DELAY_KEYS.map((k) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-wider">{k}</Label>
                <Input
                  type="number" step="0.05" min="0" max="1"
                  className="mt-1"
                  value={s.delay_multipliers[k] ?? 1}
                  onChange={(e) => setS({ ...s, delay_multipliers: { ...s.delay_multipliers, [k]: parseFloat(e.target.value) } })}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100"><CardTitle className="font-heading text-lg">Paylaşım & Prim Kuralları</CardTitle></CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Ana Usta Payı</Label>
              <Input type="number" step="0.05" className="mt-1" value={s.partnership_primary}
                onChange={(e) => setS({ ...s, partnership_primary: e.target.value })} />
              <p className="text-xs text-slate-500 mt-1">Ör: 0.7 = %70</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Yardımcı Usta Payı</Label>
              <Input type="number" step="0.05" className="mt-1" value={s.partnership_secondary}
                onChange={(e) => setS({ ...s, partnership_secondary: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Tekrar Arıza Ceza Puanı</Label>
              <Input type="number" className="mt-1" value={s.repeat_penalty}
                onChange={(e) => setS({ ...s, repeat_penalty: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">TL Çarpanı (1 Puan = ?)</Label>
              <Input type="number" step="0.1" className="mt-1" value={s.tl_multiplier}
                onChange={(e) => setS({ ...s, tl_multiplier: e.target.value })} data-testid="tl-multiplier" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Tekrar Arıza Pencere (gün)</Label>
              <Input type="number" className="mt-1" value={s.repeat_window_days}
                onChange={(e) => setS({ ...s, repeat_window_days: e.target.value })} />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function BulkImportPanel() {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewKey, setPreviewKey] = useState("");
  const [commitResult, setCommitResult] = useState(null);
  const [parseError, setParseError] = useState("");

  // ─── Yeni şema anahtarları ───
  const NEW_KEYS = new Set([
    "no", "tarih", "arac_kodu", "plaka", "ana_usta", "yardimci_usta",
    "is_seviyesi", "mesai", "dis_servis", "sofor_hatasi",
    "baslangic", "bitis", "gecikme", "sistem", "parca", "yeni_parca",
    "konumlar", "yapilan_is", "parcalar", "kontrol",
  ]);
  // Eski anahtar → yeni anahtar map'i (geriye dönük uyumluluk)
  const LEGACY_MAP = {
    date: "tarih",
    arac_plaka: "plaka",
    vehicle_plate: "plaka",
    arac_kod: "arac_kodu",
    sorumlu_personel: "ana_usta",
    sorumlu_usta: "ana_usta",
    mechanic: "ana_usta",
    yardimci: "yardimci_usta",
    job_level: "is_seviyesi",
    yapilan_is_aciklama: "yapilan_is",
    islem: "yapilan_is",
    action_taken: "yapilan_is",
    kullanilan_parca: "parca",
    parts: "parca",
    mesaiye_kaldi_mi: "mesai",
    dis_servis_mi: "dis_servis",
    outsourced: "dis_servis",
    driver_fault: "sofor_hatasi",
    sofor_kusuru: "sofor_hatasi",
    sofor_hatasi_mi: "sofor_hatasi",
    delay_reason: "gecikme",
    start_time: "baslangic",
    end_time: "bitis",
  };

  const _toBool = (v) => {
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v !== 0;
    const s = String(v || "").trim().toLowerCase();
    return s === "evet" || s === "true" || s === "1" || s === "yes" || s === "e";
  };

  // JSON input'u temizle & array'e çevir
  const stripAndParse = (text) => {
    let t = (text || "").trim();
    if (!t) throw new Error("JSON boş");
    // ```json ... ``` sarmalayıcı
    const fence = t.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
    if (fence) t = fence[1].trim();
    // ```json inline
    t = t.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/i, "").trim();
    let parsed;
    try { parsed = JSON.parse(t); }
    catch (e) { throw new Error("JSON ayrıştırılamadı: " + e.message); }
    // {"kayitlar": [...]} veya {"records": [...]} / {"data": [...]} / {"rows": [...]}
    if (parsed && !Array.isArray(parsed) && typeof parsed === "object") {
      for (const key of ["kayitlar", "records", "data", "rows", "items"]) {
        if (Array.isArray(parsed[key])) { parsed = parsed[key]; break; }
      }
    }
    if (!Array.isArray(parsed)) throw new Error("JSON bir dizi (array) olmalı");
    if (parsed.length === 0) throw new Error("Dizi boş — 0 kayıt");
    return parsed;
  };

  // Satırı normalize et: eski anahtarları map'le, bilinmeyen alanları topla
  const normaliseRow = (rec) => {
    const out = {};
    const unknown = [];
    for (const [k, v] of Object.entries(rec || {})) {
      const kn = k.trim();
      if (NEW_KEYS.has(kn)) {
        out[kn] = v;
      } else if (LEGACY_MAP[kn]) {
        out[LEGACY_MAP[kn]] = v;
      } else {
        unknown.push(kn);
      }
    }
    // Boolean dönüşümler (string "evet"/"hayır" vb. gelmişse)
    for (const k of ["mesai", "dis_servis", "sofor_hatasi", "yeni_parca"]) {
      if (k in out) out[k] = _toBool(out[k]);
    }
    // konumlar: string gelmişse diziye
    if (typeof out.konumlar === "string") {
      out.konumlar = out.konumlar.split(",").map(s => s.trim()).filter(Boolean);
    }
    // parcalar: array of {ad, adet, not} değilse boş bırak
    if (!Array.isArray(out.parcalar)) out.parcalar = [];
    return { normalised: out, unknown };
  };

  const buildPayload = () => {
    const srcArr = stripAndParse(raw);
    const rows = [];
    const frontErrors = []; // {no, reason}
    srcArr.forEach((rec, i) => {
      const no = (rec && typeof rec === "object" && rec.no) || (i + 1);
      const { normalised, unknown } = normaliseRow(rec);
      if (unknown.length > 0) {
        frontErrors.push({
          no,
          reason: `Tanınmayan alan(lar): ${unknown.join(", ")}`,
          raw: normalised,
        });
      }
      // Yine de preview'e kabul ettir — backend eksik zorunlu alanları da işaretleyecek
      rows.push({ no, ...normalised });
    });
    return { rows, frontErrors };
  };

  const onPreview = async () => {
    setParseError("");
    setCommitResult(null);
    setPreview(null);
    let payload;
    try {
      payload = buildPayload();
    } catch (e) {
      setParseError(`Metin okunamadı: ${e.message}`);
      toast.error(`Metin okunamadı: ${e.message}`);
      return;
    }
    if (payload.rows.length === 0) {
      setParseError("Metin okunamadı: 0 satır");
      toast.error("Metin okunamadı: 0 satır");
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await api.post("/work-orders/bulk-preview", { rows: payload.rows });
      // Frontend-level "tanınmayan alan" hatalarını, backend sonucu üzerinde ÜST et
      const errMap = new Map(payload.frontErrors.map(e => [e.no, e.reason]));
      const rows = (data.rows || []).map(r => {
        if (errMap.has(r.no)) {
          return { ...r, status: "error", reason: `${errMap.get(r.no)}${r.reason ? ` · ${r.reason}` : ""}` };
        }
        return r;
      });
      const counts = { total: rows.length, add: 0, duplicate: 0, error: 0, locked: 0 };
      for (const r of rows) counts[r.status] = (counts[r.status] || 0) + 1;
      const next = { ...data, rows, summary: { ...data.summary, counts } };
      setPreview(next);
      setPreviewKey(raw); // commit'te metin değişmişse engelle
      toast.success(`Önizleme: ${counts.add} eklenecek · ${counts.duplicate} mükerrer · ${counts.error} hata · ${counts.locked} kilitli`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || e.message || "Önizleme hatası");
      setParseError(msg);
      toast.error(msg);
    } finally { setPreviewing(false); }
  };

  const onCommit = async () => {
    if (!preview) return;
    if (previewKey !== raw) {
      toast.error("JSON değişti — tekrar Önizle"); return;
    }
    const addCount = preview.summary?.counts?.add || 0;
    if (addCount === 0) { toast.error("Eklenecek kayıt yok"); return; }
    if (!window.confirm(`${addCount} iş emri eklenecek. Devam?`)) return;
    setBusy(true); setCommitResult(null);
    try {
      const { rows } = buildPayload();
      const { data } = await api.post("/work-orders/bulk-commit", { rows });
      setCommitResult(data);
      toast.success(`Aktarım: ${data.created} eklendi · ${data.skipped_duplicate} mükerrer · ${data.errors?.length || 0} hata`);
      setPreview(null); setPreviewKey("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || e.message || "Aktarım hatası");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const canCommit = !!preview && previewKey === raw && (preview.summary?.counts?.add || 0) > 0;

  const STATUS_META = {
    add:       { label: "Eklenecek",             cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    duplicate: { label: "Mükerrer — atlanacak",  cls: "bg-slate-100 text-slate-700 border-slate-200" },
    error:     { label: "Hata",                  cls: "bg-red-100 text-red-800 border-red-200" },
    locked:    { label: "Kilitli ay",            cls: "bg-amber-100 text-amber-800 border-amber-200" },
  };

  return (
    <div className="space-y-4">
      <div className="text-xs text-slate-500 leading-relaxed">
        İş emri kayıtlarını JSON dizisi olarak yapıştırın (markdown wrapper <code className="text-[10px]">```json</code> ve <code className="text-[10px]">{`{"kayitlar":[...]}`}</code> sarmalayıcı otomatik soyulur).
        Yeni şema anahtarları: <code className="text-[10px]">no, tarih, arac_kodu, plaka, ana_usta, yardimci_usta, is_seviyesi, mesai, dis_servis, baslangic, bitis, gecikme, sistem, parca, yeni_parca, konumlar, yapilan_is, parcalar, kontrol</code>.
        Eski anahtarlar (<code className="text-[10px]">arac_plaka, sorumlu_personel, yapilan_is_aciklama, kullanilan_parca, mesaiye_kaldi_mi, dis_servis_mi</code>) otomatik map'lenir. <b>Önce Önizle</b>, sonra <b>İçe Aktar</b>.
      </div>
      <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="bulk-webhook-note">
        <b>⚠ Webhook atma:</b> Bu akışla eklenen kayıtlar <b>Yazturizm'e GÖNDERİLMEZ</b> (eski/geçmiş kayıtlar için). Durum "Gönderilmedi" olarak kaydedilir — gerekirse İş Emirleri ekranından admin elle gönderebilir.
      </div>
      <Textarea
        value={raw}
        onChange={(e) => { setRaw(e.target.value); }}
        placeholder='[{"no":1,"tarih":"2026-11-05","arac_kodu":"538","plaka":"16 YT 904","ana_usta":"Musa Muzaç","is_seviyesi":"Orta İş","mesai":false,"dis_servis":false,"baslangic":"09:00","bitis":"10:00","gecikme":"Gecikme Yok","sistem":"Fren","parca":"Balata","konumlar":["Ön Sol"],"yapilan_is":"...","parcalar":[]}]'
        rows={10}
        className="font-mono text-xs"
        data-testid="bulk-import-textarea"
      />
      {parseError && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 font-mono" data-testid="bulk-parse-error">
          {parseError}
        </div>
      )}
      <div className="flex items-center gap-2 justify-end">
        <Button
          onClick={onPreview}
          disabled={previewing || !raw.trim()}
          className="bg-slate-800 hover:bg-slate-900 text-white"
          data-testid="bulk-preview-btn"
        >
          {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
          Önizle
        </Button>
        <Button
          onClick={onCommit}
          disabled={!canCommit || busy}
          className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
          data-testid="bulk-import-run-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
          İçe Aktar{preview ? ` (${preview.summary?.counts?.add || 0})` : ""}
        </Button>
      </div>

      {preview && (
        <div className="space-y-3" data-testid="bulk-preview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="bulk-counts">
            <KpiTile label="Eklenecek" val={preview.summary.counts.add || 0} tone="emerald" />
            <KpiTile label="Mükerrer" val={preview.summary.counts.duplicate || 0} tone="slate" />
            <KpiTile label="Hatalı" val={preview.summary.counts.error || 0} tone="red" />
            <KpiTile label="Kilitli ay" val={preview.summary.counts.locked || 0} tone="amber" />
          </div>
          {Object.keys(preview.summary.pool_penalty_after || {}).length > 0 && (
            <div className="text-xs font-mono bg-slate-50 border border-slate-200 rounded p-2 space-y-0.5" data-testid="bulk-pool-summary">
              <div className="font-sans font-semibold text-slate-700 text-xs mb-1">
                Havuz kesinti etkisi (olay × ₺{(preview.summary.pool_penalty_per_incident_tl || 0).toLocaleString("tr-TR")} — kural: mesai & şoför-hatası-değil):
              </div>
              {Object.keys(preview.summary.pool_penalty_after).map((m) => {
                const b = preview.summary.pool_penalty_before[m] || { count: 0, tl: 0 };
                const a = preview.summary.pool_penalty_after[m];
                const diff = (a.count || 0) - (b.count || 0);
                return (
                  <div key={m} className="text-slate-700">
                    <b>{m}:</b> {b.count} ({(b.tl || 0).toLocaleString("tr-TR")}₺) → <b>{a.count}</b> ({(a.tl || 0).toLocaleString("tr-TR")}₺)
                    {diff > 0 && <span className="ml-2 text-red-700">+{diff}</span>}
                  </div>
                );
              })}
            </div>
          )}
          {(preview.summary.new_systems?.length > 0 || preview.summary.new_parts?.length > 0) && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2">
              <div className="font-semibold text-amber-800 mb-1">Aktar'da otomatik oluşturulacaklar:</div>
              {preview.summary.new_systems.map((s) => (
                <div key={`sys-${s}`} className="font-mono text-amber-800">· Yeni sistem: {s}</div>
              ))}
              {preview.summary.new_parts.map((p, i) => (
                <div key={`part-${i}`} className="font-mono text-amber-800">· Yeni parça: {p.system} → {p.part}</div>
              ))}
            </div>
          )}
          <div className="overflow-x-auto border border-slate-200 rounded">
            <Table data-testid="bulk-preview-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px] uppercase">No</TableHead>
                  <TableHead className="text-[10px] uppercase">Tarih</TableHead>
                  <TableHead className="text-[10px] uppercase">Araç</TableHead>
                  <TableHead className="text-[10px] uppercase">Ana Usta</TableHead>
                  <TableHead className="text-[10px] uppercase">Yardımcı</TableHead>
                  <TableHead className="text-[10px] uppercase">İş Sev.</TableHead>
                  <TableHead className="text-[10px] uppercase">Sistem · Parça · Konum</TableHead>
                  <TableHead className="text-[10px] uppercase text-center">Mesai</TableHead>
                  <TableHead className="text-[10px] uppercase text-center">Dış Servis</TableHead>
                  <TableHead className="text-[10px] uppercase text-right">Süre</TableHead>
                  <TableHead className="text-[10px] uppercase">Gecikme</TableHead>
                  <TableHead className="text-[10px] uppercase text-right">Puan</TableHead>
                  <TableHead className="text-[10px] uppercase">Durum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.rows.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.error;
                  return (
                    <TableRow key={r.no} data-testid={`bulk-row-${r.no}`}>
                      <TableCell className="font-mono text-xs">{r.no}</TableCell>
                      <TableCell className="font-mono text-xs">{r.tarih}</TableCell>
                      <TableCell className="font-mono text-xs">{r.arac_kodu}/{r.plaka}</TableCell>
                      <TableCell className="text-xs">{r.ana_usta}</TableCell>
                      <TableCell className="text-xs">{r.yardimci_usta || "—"}</TableCell>
                      <TableCell className="text-xs">{r.is_seviyesi}</TableCell>
                      <TableCell className="text-xs">{(r.sistem || "—")} · {(r.parca || "—")} · {r.konum_str}</TableCell>
                      <TableCell className="text-center text-xs">{r.mesai ? "✓" : "—"}</TableCell>
                      <TableCell className="text-center text-xs" data-testid={`bulk-dis-servis-${r.no}`}>{r.dis_servis ? "✓" : "—"}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{r.duration_minutes}dk</TableCell>
                      <TableCell className="text-xs">{r.gecikme}</TableCell>
                      <TableCell className="text-right font-mono text-xs font-semibold">{r.estimated_score}</TableCell>
                      <TableCell>
                        <div className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${meta.cls}`} data-testid={`bulk-status-${r.no}`}>
                          {r.status === "add" && <CheckCircle2 className="w-3 h-3" />}
                          {r.status === "duplicate" && <SkipForward className="w-3 h-3" />}
                          {r.status === "error" && <AlertTriangle className="w-3 h-3" />}
                          {r.status === "locked" && <Lock className="w-3 h-3" />}
                          {meta.label}
                        </div>
                        {r.reason && <div className="text-[10px] text-red-700 mt-0.5" data-testid={`bulk-reason-${r.no}`}>{r.reason}</div>}
                        {r.warnings?.map((w, i) => (
                          <div key={i} className="text-[10px] text-amber-700 mt-0.5">
                            <AlertTriangle className="w-2.5 h-2.5 inline mr-1" />{w}
                          </div>
                        ))}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {commitResult && (
        <div className="border border-emerald-200 rounded-lg p-3 bg-emerald-50 text-sm space-y-1" data-testid="bulk-import-result">
          <div><strong className="text-emerald-700">{commitResult.created}</strong> yeni iş emri eklendi · <strong>{commitResult.skipped_duplicate}</strong> mükerrer · <strong>{commitResult.skipped_locked}</strong> kilitli ay · <strong className="text-rose-700">{commitResult.errors?.length || 0}</strong> hata</div>
          {commitResult.created_systems?.length > 0 && (
            <div className="text-xs text-amber-800">Yeni sistem(ler): {commitResult.created_systems.map(s => s.name).join(", ")}</div>
          )}
          {commitResult.created_parts?.length > 0 && (
            <div className="text-xs text-amber-800">Yeni parça(lar): {commitResult.created_parts.map(p => `${p.system}→${p.part}`).join(", ")}</div>
          )}
          <div className="text-[10px] text-slate-500 font-mono">audit: {commitResult.audit_id}</div>
          {commitResult.errors?.length > 0 && (
            <details className="mt-1">
              <summary className="cursor-pointer text-rose-700 text-xs">Hatalar ({commitResult.errors.length})</summary>
              <ul className="text-xs mt-1 space-y-0.5 font-mono">
                {commitResult.errors.map((er, i) => (<li key={i}>#{er.no}: {er.reason}</li>))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function KpiTile({ label, val, tone }) {
  const toneCls = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    slate:   "border-slate-200 bg-slate-50 text-slate-700",
    red:     "border-red-200 bg-red-50 text-red-700",
    amber:   "border-amber-200 bg-amber-50 text-amber-800",
  }[tone] || "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <div className={`border rounded-md p-2 ${toneCls}`}>
      <div className="text-[10px] uppercase font-mono opacity-80">{label}</div>
      <div className="text-xl font-bold">{val}</div>
    </div>
  );
}

function WebhookHistoryPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/work-orders/webhook/history", { params: { limit: 100 } });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Geçmiş yüklenemedi");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const BADGE = {
    sent:     "bg-emerald-100 text-emerald-800 border-emerald-200",
    failed:   "bg-red-100 text-red-800 border-red-200",
    not_sent: "bg-slate-100 text-slate-700 border-slate-200",
    pending:  "bg-amber-100 text-amber-800 border-amber-200",
  };
  const LABEL = {
    sent: "Gönderildi", failed: "Başarısız", not_sent: "Gönderilmedi", pending: "Beklemede",
  };
  return (
    <div className="space-y-3" data-testid="webhook-history-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          Yazturizm'e yapılan son 100 gönderim denemesi. URL ve token hiçbir yerde gösterilmez.
        </p>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="webhook-history-refresh">
          <RefreshCw className={`w-3 h-3 mr-2 ${loading ? "animate-spin" : ""}`} /> Yenile
        </Button>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded">
        <table className="w-full text-xs" data-testid="webhook-history-table">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-2 py-1.5">Tarih</th>
              <th className="text-left px-2 py-1.5">Kaynak</th>
              <th className="text-left px-2 py-1.5">Kayıt</th>
              <th className="text-left px-2 py-1.5">Araç</th>
              <th className="text-left px-2 py-1.5">Sonuç</th>
              <th className="text-left px-2 py-1.5">HTTP</th>
              <th className="text-left px-2 py-1.5">Deneme Zamanı</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center text-slate-400 py-6">Henüz gönderim denemesi yok</td></tr>
            )}
            {items.map((it) => (
              <tr key={`${it.kind}-${it.id}`} className="border-t border-slate-100" data-testid={`wh-hist-${it.id}`}>
                <td className="px-2 py-1 font-mono">{(it.date || "").slice(0, 10)}</td>
                <td className="px-2 py-1" data-testid={`wh-kind-${it.id}`}>
                  {it.kind === "dis_servis"
                    ? <span className="inline-block border border-orange-300 bg-orange-50 text-orange-700 rounded-full px-2 py-0.5 text-[10px] font-bold">Dış Servis</span>
                    : <span className="text-[10px] text-slate-600">İş Emri</span>}
                </td>
                <td className="px-2 py-1 font-mono text-[10px] text-slate-600">{it.id.slice(0, 8)}…</td>
                <td className="px-2 py-1">{it.vehicle_code ? `${it.vehicle_code} / ` : ""}{it.vehicle_plate}</td>
                <td className="px-2 py-1">
                  <span className={`inline-block border rounded-full px-2 py-0.5 text-[10px] font-bold ${BADGE[it.webhook_status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                    {LABEL[it.webhook_status] || it.webhook_status || "—"}
                  </span>
                </td>
                <td className="px-2 py-1 font-mono">{it.webhook_http_code || "—"}</td>
                <td className="px-2 py-1 font-mono text-slate-600">{it.webhook_last_attempt_at ? new Date(it.webhook_last_attempt_at).toLocaleString("tr-TR") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BulkShiftImportPanel() {
  // Update 29.17 — Önizle → İçe Aktar iki aşamalı akış.
  const [raw, setRaw] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewKey, setPreviewKey] = useState("");
  const [commitResult, setCommitResult] = useState(null);
  const [parseError, setParseError] = useState("");

  // JSON input'u temizle & { vardiya_kayitlari, gunluk_notlar } yapısına indirge
  const stripAndParse = (text) => {
    let t = (text || "").trim();
    if (!t) throw new Error("JSON boş");
    const fence = t.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
    if (fence) t = fence[1].trim();
    t = t.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/i, "").trim();
    let parsed;
    try { parsed = JSON.parse(t); }
    catch (e) { throw new Error("JSON ayrıştırılamadı: " + e.message); }
    // Düz dizi → vardiya_kayitlari = parsed
    if (Array.isArray(parsed)) return { vardiya_kayitlari: parsed, gunluk_notlar: [] };
    if (!parsed || typeof parsed !== "object") throw new Error("JSON bir nesne veya dizi olmalı");
    // vardiya_kayitlari zaten varsa olduğu gibi
    if (Array.isArray(parsed.vardiya_kayitlari)) {
      return {
        vardiya_kayitlari: parsed.vardiya_kayitlari,
        gunluk_notlar: Array.isArray(parsed.gunluk_notlar) ? parsed.gunluk_notlar : [],
      };
    }
    // Alternatif sarmalayıcılar
    for (const key of ["kayitlar", "records", "data", "rows", "items"]) {
      if (Array.isArray(parsed[key])) {
        return {
          vardiya_kayitlari: parsed[key],
          gunluk_notlar: Array.isArray(parsed.gunluk_notlar) ? parsed.gunluk_notlar : [],
        };
      }
    }
    throw new Error("JSON içinde `vardiya_kayitlari` dizisi bulunamadı");
  };

  const buildBody = () => {
    const body = stripAndParse(raw);
    if (!Array.isArray(body.vardiya_kayitlari) || body.vardiya_kayitlari.length === 0) {
      throw new Error("0 kayıt — en az bir gün (tarih) olmalı");
    }
    return body;
  };

  const onPreview = async () => {
    setParseError("");
    setCommitResult(null);
    setPreview(null);
    let body;
    try { body = buildBody(); }
    catch (e) {
      setParseError(`Metin okunamadı: ${e.message}`);
      toast.error(`Metin okunamadı: ${e.message}`);
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await api.post("/shifts/bulk-preview", body);
      if (!data || !Array.isArray(data.rows) || data.rows.length === 0) {
        setParseError("Metin okunamadı: 0 satır");
        toast.error("Metin okunamadı: 0 satır");
        return;
      }
      setPreview(data);
      setPreviewKey(raw);
      const c = data.summary?.counts || {};
      toast.success(
        `Önizleme: ${c.new || 0} yeni · ${c.change || 0} değişecek · ${c.same || 0} aynı · ${c.error || 0} hata`
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || e.message || "Önizleme hatası");
      setParseError(msg);
      toast.error(msg);
    } finally { setPreviewing(false); }
  };

  const onCommit = async () => {
    if (!preview) return;
    if (previewKey !== raw) { toast.error("JSON değişti — tekrar Önizle"); return; }
    const c = preview.summary?.counts || {};
    if (!c.new && !c.change) { toast.error("Eklenecek veya değişecek kayıt yok"); return; }
    let body;
    try { body = buildBody(); }
    catch (e) { toast.error(e.message); return; }
    if (c.change > 0) {
      const ok = window.confirm(
        `${c.change} mevcut vardiya kaydının üzerine yazılacak, devam edilsin mi?`
      );
      if (!ok) return;
      body.confirm_overwrite = true;
    }
    setCommitting(true); setCommitResult(null);
    try {
      const { data } = await api.post("/shifts/bulk-commit", body);
      setCommitResult(data);
      toast.success(
        `Aktarım: ${data.added} eklendi · ${data.changed} değişti · ${data.same} aynı · ${data.errors?.length || 0} hata`
      );
      setPreview(null); setPreviewKey("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || e.message || "Aktarım hatası");
      toast.error(msg);
    } finally { setCommitting(false); }
  };

  const canCommit = !!preview && previewKey === raw &&
    ((preview.summary?.counts?.new || 0) + (preview.summary?.counts?.change || 0)) > 0;

  const STATUS_META = {
    new:    { label: "Yeni",             cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    change: { label: "Değişecek",        cls: "bg-orange-100 text-orange-800 border-orange-200" },
    same:   { label: "Aynı, atlanacak",  cls: "bg-slate-100 text-slate-700 border-slate-200" },
    error:  { label: "Hata",             cls: "bg-red-100 text-red-800 border-red-200" },
  };

  const diffCell = (oldV, newV, status) => {
    if (status !== "change" || !oldV) return <span className="font-mono">{newV || "—"}</span>;
    const changed = (oldV || "") !== (newV || "");
    return (
      <span className="font-mono">
        {changed ? (
          <>
            <span className="line-through text-slate-400">{oldV || "—"}</span>
            <span className="mx-1 text-orange-700">→</span>
            <span className="text-orange-800 font-semibold">{newV || "—"}</span>
          </>
        ) : (newV || "—")}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="text-xs text-slate-500 leading-relaxed">
        Günlük vardiya çizelgelerini ve günlük notları toplu olarak <b>önizleyin</b>, sonra <b>içe aktarın</b>.
        Düz dizi, <code className="text-[10px]">{`{vardiya_kayitlari:[...]}`}</code>, <code className="text-[10px]">{`{kayitlar:[...]}`}</code> veya <code className="text-[10px]">{"```json … ```"}</code> sarmalayıcı otomatik soyulur.
        Alanlar: <code className="text-[10px]">tarih, hat_cizelgeleri:[{"{hat, araclar:[{sira, plaka_kod, sabah_sofor, degisim_saati, aksam_sofor, durum}]}"}], gunluk_notlar:[...]</code>.
        <code className="text-[10px]"> degisim_saati="Yatar"</code> → <code className="text-[10px]">YATAR</code>; <code className="text-[10px]">durum="Sabah Yatar"</code> sabah şoförünü boşaltır. Tanınmayan alan içeren satırlar <b>sessizce atlanmaz</b>; önizlemede "Tanınmayan alan(lar): …" olarak işaretlenir.
      </div>
      <Textarea
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        placeholder='{"vardiya_kayitlari":[{"tarih":"2026-08-01","hat_cizelgeleri":[{"hat":"15A","araclar":[{"sira":1,"plaka_kod":"16 ADJ 678","sabah_sofor":"Mehmet Hazar","degisim_saati":"-","aksam_sofor":"Mehmet Hazar","durum":"Tek Şoför"}]}]}],"gunluk_notlar":[]}'
        rows={10}
        className="font-mono text-xs"
        data-testid="bulk-shift-import-textarea"
      />
      {parseError && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 font-mono" data-testid="bulk-shift-parse-error">
          {parseError}
        </div>
      )}
      <div className="flex items-center gap-2 justify-end">
        <Button
          onClick={onPreview}
          disabled={previewing || !raw.trim()}
          className="bg-slate-800 hover:bg-slate-900 text-white"
          data-testid="bulk-shift-preview-btn"
        >
          {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
          Önizle
        </Button>
        <Button
          onClick={onCommit}
          disabled={!canCommit || committing}
          className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
          data-testid="bulk-shift-import-run-btn"
        >
          {committing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
          İçe Aktar{preview ? ` (${(preview.summary?.counts?.new || 0) + (preview.summary?.counts?.change || 0)})` : ""}
        </Button>
      </div>

      {preview && (
        <div className="space-y-3" data-testid="bulk-shift-preview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="bulk-shift-counts">
            <KpiTile label="Yeni" val={preview.summary.counts.new || 0} tone="emerald" />
            <KpiTile label="Değişecek" val={preview.summary.counts.change || 0} tone="amber" />
            <KpiTile label="Aynı" val={preview.summary.counts.same || 0} tone="slate" />
            <KpiTile label="Hatalı" val={preview.summary.counts.error || 0} tone="red" />
          </div>
          {(preview.summary.affected_dates || []).length > 0 && (
            <div className="text-xs bg-indigo-50 border border-indigo-200 rounded p-2 font-mono" data-testid="bulk-shift-affected-dates">
              <b className="text-indigo-800">Etkilenecek tarihler:</b>{" "}
              {preview.summary.affected_dates.join(", ")}
            </div>
          )}
          {(preview.day_notes_preview || []).length > 0 && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 space-y-1" data-testid="bulk-shift-day-notes">
              <div className="font-semibold text-amber-800">Günlük notlar:</div>
              {preview.day_notes_preview.map((dn, i) => (
                <div key={i} className="font-mono text-[11px] text-amber-900">
                  · {dn.date}: {dn.status === "same" ? "aynı" : dn.status === "new" ? "yeni" : "değişecek"}
                </div>
              ))}
            </div>
          )}
          <div className="overflow-x-auto border border-slate-200 rounded">
            <Table data-testid="bulk-shift-preview-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px] uppercase">No</TableHead>
                  <TableHead className="text-[10px] uppercase">Tarih</TableHead>
                  <TableHead className="text-[10px] uppercase">Hat</TableHead>
                  <TableHead className="text-[10px] uppercase">Araç / Plaka</TableHead>
                  <TableHead className="text-[10px] uppercase">1. Vardiya</TableHead>
                  <TableHead className="text-[10px] uppercase">Değişim</TableHead>
                  <TableHead className="text-[10px] uppercase">2. Vardiya</TableHead>
                  <TableHead className="text-[10px] uppercase">Not</TableHead>
                  <TableHead className="text-[10px] uppercase">Durum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.rows.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.error;
                  return (
                    <TableRow key={r.no} data-testid={`bulk-shift-row-${r.no}`}>
                      <TableCell className="font-mono text-xs">{r.no}</TableCell>
                      <TableCell className="font-mono text-xs">{r.tarih || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{r.line || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{r.vehicle_no ?? "—"} / {r.plate || "—"}</TableCell>
                      <TableCell className="text-xs">
                        {diffCell(r.old?.shift1_personnel, r.new?.shift1_personnel, r.status)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {diffCell(r.old?.change_time, r.new?.change_time, r.status)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {diffCell(r.old?.shift2_personnel, r.new?.shift2_personnel, r.status)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {diffCell(r.old?.notes, r.new?.notes, r.status)}
                      </TableCell>
                      <TableCell>
                        <div className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${meta.cls}`} data-testid={`bulk-shift-status-${r.no}`}>
                          {r.status === "new" && <CheckCircle2 className="w-3 h-3" />}
                          {r.status === "change" && <AlertTriangle className="w-3 h-3" />}
                          {r.status === "same" && <SkipForward className="w-3 h-3" />}
                          {r.status === "error" && <XCircle className="w-3 h-3" />}
                          {meta.label}
                        </div>
                        {r.reason && <div className="text-[10px] text-red-700 mt-0.5" data-testid={`bulk-shift-reason-${r.no}`}>{r.reason}</div>}
                        {(r.warnings || []).map((w, i) => (
                          <div key={i} className="text-[10px] text-amber-700 mt-0.5">
                            <AlertTriangle className="w-2.5 h-2.5 inline mr-1" />{w}
                          </div>
                        ))}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {commitResult && (
        <div className="border border-emerald-200 rounded-lg p-3 bg-emerald-50 text-sm space-y-1" data-testid="bulk-shift-result">
          <div>
            <strong className="text-emerald-700">{commitResult.added}</strong> eklendi · <strong className="text-orange-700">{commitResult.changed}</strong> değişti · <strong>{commitResult.same}</strong> aynı · <strong className="text-rose-700">{commitResult.errors?.length || 0}</strong> hata
          </div>
          <div className="text-[10px] text-slate-500 font-mono">audit: {commitResult.audit_id}</div>
          {commitResult.errors?.length > 0 && (
            <details className="mt-1">
              <summary className="cursor-pointer text-rose-700 text-xs">Hatalar ({commitResult.errors.length})</summary>
              <ul className="text-xs mt-1 space-y-0.5 font-mono">
                {commitResult.errors.map((er, i) => (<li key={i}>#{er.no}: {er.reason}</li>))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}


/* -----------------------------------------------------------
 * NetgsmHealthPanel — bakiye + onaylı başlıklar + test SMS aracı
 * -----------------------------------------------------------*/
function NetgsmHealthPanel({ configured_header, onTestSent }) {
  const [balance, setBalance] = useState(null);
  const [headers, setHeaders] = useState(null);
  const [loadingBal, setLoadingBal] = useState(false);
  const [loadingHdr, setLoadingHdr] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);

  const loadBalance = async () => {
    setLoadingBal(true);
    try { const { data } = await api.get("/settings/netgsm/balance"); setBalance(data); }
    catch (e) { setBalance({ error: e.response?.data?.detail || "Bakiye sorgu hatası" }); }
    finally { setLoadingBal(false); }
  };
  const loadHeaders = async () => {
    setLoadingHdr(true);
    try { const { data } = await api.get("/settings/netgsm/headers"); setHeaders(data); }
    catch (e) { setHeaders({ error: e.response?.data?.detail || "Header sorgu hatası" }); }
    finally { setLoadingHdr(false); }
  };
  useEffect(() => { loadBalance(); loadHeaders(); }, []);

  const sendTest = async () => {
    const p = testPhone.trim();
    if (!p) { toast.error("Telefon numarası girin"); return; }
    setSendingTest(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/settings/netgsm/test-sms", { phone: p });
      setTestResult(data);
      if (data.ok) toast.success(`Test SMS gönderildi — Netgsm kod: ${data.code}`);
      else toast.error(`Netgsm Hata Kodu: ${data.code} — ${data.label}`);
      onTestSent?.();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Hata";
      setTestResult({ ok: false, code: "CLIENT", label: detail });
      toast.error(detail);
    } finally { setSendingTest(false); }
  };

  return (
    <div className="mb-5 rounded-lg border border-slate-200 bg-gradient-to-br from-slate-50 to-white overflow-hidden" data-testid="netgsm-health-panel">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-100/60 text-[10px] font-mono uppercase tracking-widest text-slate-600 flex items-center justify-between">
        <span>📡 Netgsm Sağlık & Test Araçları</span>
        <div className="flex gap-1">
          <button onClick={() => { loadBalance(); loadHeaders(); }} className="text-slate-400 hover:text-slate-700" title="Yenile" data-testid="netgsm-health-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Balance card */}
        <BalanceBadge balance={balance} loading={loadingBal} />
        {/* Approved headers card */}
        <HeadersBadge headers={headers} loading={loadingHdr} configured={configured_header} />
        {/* Test SMS card */}
        <div className="rounded border border-blue-200 bg-blue-50 p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-blue-700 flex items-center gap-1">
            <Send className="w-3 h-3" /> Test SMS Gönder
          </div>
          <div className="mt-2 flex gap-1">
            <Input
              type="tel"
              inputMode="tel"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="Netgsm hesap telefonu"
              className="h-8 font-mono text-xs"
              data-testid="netgsm-test-phone-input"
              disabled={sendingTest}
            />
            <Button
              size="sm"
              onClick={sendTest}
              disabled={sendingTest || !testPhone.trim()}
              className="h-8 px-2 bg-blue-600 hover:bg-blue-700 text-white text-xs shrink-0"
              data-testid="netgsm-test-send-btn"
            >
              {sendingTest ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Phone className="w-3 h-3 mr-1" />Gönder</>}
            </Button>
          </div>
          {testResult && (
            <div className={`mt-2 text-[11px] leading-tight rounded p-2 border ${testResult.ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-red-300 bg-red-50 text-red-800"}`} data-testid="netgsm-test-result">
              <div className="flex items-center gap-1 font-medium">
                {testResult.ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                Netgsm Error Code: <code className="font-mono">{testResult.code}</code>
              </div>
              <div className="text-[10px] mt-0.5">{testResult.label}</div>
              {testResult.raw && <div className="text-[10px] mt-0.5 opacity-70">Raw: <code className="break-all">{testResult.raw.slice(0, 80)}</code></div>}
              {testResult.test_code && <div className="text-[10px] mt-0.5">Test kodu: <b>{testResult.test_code}</b></div>}
            </div>
          )}
          <p className="mt-2 text-[10px] text-blue-700 leading-tight">
            Fail-safe her zaman aktif: <b>123456</b> ve telefon son 4 hanesi.
          </p>
        </div>
      </div>
    </div>
  );
}

function BalanceBadge({ balance, loading }) {
  if (loading) return <SkeletonCard title="💰 Netgsm Bakiye" />;
  if (!balance || balance.error) {
    return (
      <div className="rounded border border-slate-200 bg-white p-3" data-testid="netgsm-balance-card">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1"><Wallet className="w-3 h-3" /> Netgsm Bakiye</div>
        <div className="mt-1 text-xs text-slate-500 italic">{balance?.error || "Yüklenemedi"}</div>
      </div>
    );
  }
  const credit = balance.credit_tl || {};
  const packet = balance.packet_sms || {};
  const creditOk = credit.ok;
  const packetOk = packet.ok;
  return (
    <div className={`rounded border p-3 ${creditOk ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="netgsm-balance-card">
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-600 flex items-center gap-1"><Wallet className="w-3 h-3" /> Netgsm Bakiye</div>
      {creditOk ? (
        <div className="mt-1">
          <div className="font-mono font-bold text-xl text-emerald-800">{credit.raw}</div>
          <div className="text-[10px] text-slate-600 mt-0.5">TL kredi bakiyesi</div>
        </div>
      ) : (
        <div className="mt-1 text-[11px] text-amber-800">
          <div className="font-medium">TL Bakiye: {credit.label || "sorgulanamadı"}</div>
          {credit.raw && <div className="opacity-70 mt-0.5">Raw: {credit.raw.slice(0, 40)}</div>}
        </div>
      )}
      <div className={`mt-2 pt-2 border-t text-[11px] ${creditOk ? "border-emerald-200" : "border-amber-200"}`}>
        <div className="flex items-center gap-1 text-slate-700">
          <Package className="w-3 h-3" /> Paket SMS: {packetOk ? <b className="text-emerald-700 font-mono">{packet.raw}</b> : <span className="text-amber-700">{packet.label || packet.raw?.slice(0, 30)}</span>}
        </div>
      </div>
    </div>
  );
}

function HeadersBadge({ headers, loading, configured }) {
  if (loading) return <SkeletonCard title="📛 Onaylı Başlıklar" />;
  if (!headers || headers.error) {
    return (
      <div className="rounded border border-slate-200 bg-white p-3" data-testid="netgsm-headers-card">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1">📛 Onaylı Başlıklar</div>
        <div className="mt-1 text-xs text-slate-500 italic">{headers?.error || "Yüklenemedi"}</div>
      </div>
    );
  }
  const approved = headers.configured_approved;
  return (
    <div className={`rounded border p-3 ${headers.ok ? (approved ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50") : "border-red-200 bg-red-50"}`} data-testid="netgsm-headers-card">
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-600 flex items-center gap-1">📛 Onaylı Başlıklar</div>
      {headers.ok ? (
        <>
          <div className="mt-1 flex items-center gap-1 text-xs">
            {approved ? (
              <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" /> <span className="font-mono font-bold text-emerald-800">{configured}</span> <span className="text-emerald-700">onaylı ✓</span></>
            ) : configured ? (
              <><XCircle className="w-3.5 h-3.5 text-amber-700" /> <span className="font-mono font-bold text-amber-800">{configured}</span> <span className="text-amber-700">listede yok!</span></>
            ) : (
              <span className="text-slate-500 italic">Kayıtlı başlık boş</span>
            )}
          </div>
          <div className="mt-2 text-[10px] text-slate-600 flex flex-wrap gap-1">
            {headers.headers.slice(0, 12).map((h) => (
              <span key={h} className={`px-1.5 py-0.5 rounded font-mono text-[10px] ${h === configured ? "bg-emerald-200 text-emerald-900 font-bold" : "bg-white border border-slate-200 text-slate-600"}`}>{h}</span>
            ))}
            {headers.headers.length > 12 && (<span className="text-slate-500">+{headers.headers.length - 12}</span>)}
          </div>
        </>
      ) : (
        <div className="mt-1 text-xs text-red-800">
          <div className="font-medium">Hata Kodu: {headers.error_code}</div>
          <div className="text-[10px] opacity-70 mt-0.5">{headers.error_label}</div>
        </div>
      )}
    </div>
  );
}

function SkeletonCard({ title }) {
  return (
    <div className="rounded border border-slate-200 bg-slate-50 p-3 animate-pulse">
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{title}</div>
      <div className="mt-2 h-4 bg-slate-200 rounded w-1/2" />
      <div className="mt-2 h-3 bg-slate-200 rounded w-3/4" />
    </div>
  );
}


// =============================================================
// Update 24 — Hariç tutulan personel paneli
// =============================================================
function ExcludedPersonnelPanel() {
  const [data, setData] = React.useState({ count: 0, items: [] });
  const [busy, setBusy] = React.useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/personnel/excluded");
      setData(data);
    } catch {}
  };
  React.useEffect(() => { load(); }, []);

  const removeOne = async (hash) => {
    if (!window.confirm("Bu kayıt hariç tutulan listeden çıkarılacak. Aynı TC ile Excel içe aktarma yeniden mümkün olacak. Devam?")) return;
    setBusy(true);
    try {
      await api.delete(`/personnel/excluded/${hash}`);
      toast.success("Listeden çıkarıldı");
      await load();
    } catch {
      toast.error("Çıkarılamadı");
    } finally { setBusy(false); }
  };

  return (
    <Card className="border-slate-200 shadow-none" data-testid="excluded-panel">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <ShieldOff className="w-4 h-4" style={{ color: "var(--y-danger)" }} />
          Hariç Tutulan Personel
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="flex items-baseline justify-between mb-3">
          <p className="text-xs text-slate-500 pr-4">
            Kalıcı olarak silinmiş personelin <b>tek yönlü TC hash'i</b> burada saklanır.
            Excel içe aktarmada bu hash'e uyan satırlar önizlemede "Hariç tutulan kişi — atlanacak"
            olarak görünür ve içe aktarılmaz. Düz TC veya isim SAKLANMAZ.
          </p>
          <span
            className="shrink-0 rounded-md px-3 py-1 mono font-bold"
            style={{ background: "var(--y-surface-2)", color: "var(--y-text)", fontSize: 14 }}
            data-testid="excluded-count"
          >
            {data.count}
          </span>
        </div>
        {data.count === 0 ? (
          <div className="text-sm py-4 text-center" style={{ color: "var(--y-muted)" }}>
            Hariç tutulan kayıt yok.
          </div>
        ) : (
          <ul className="divide-y divide-slate-200 border border-slate-200 rounded-md overflow-hidden">
            {data.items.map((it) => (
              <li key={it.hash} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`excluded-item-${it.hash_short}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <ShieldOff className="w-4 h-4 shrink-0" style={{ color: "var(--y-danger)" }} />
                  <div>
                    <div className="mono text-xs" style={{ color: "var(--y-muted)" }} data-testid="excluded-hash">
                      #{it.hash_short}
                    </div>
                    <div className="text-xs" style={{ color: "var(--y-text-2)" }}>
                      Silinme: <span className="mono">{(it.deleted_at || "").slice(0, 10)}</span>
                    </div>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => removeOne(it.hash)}
                  data-testid={`excluded-remove-${it.hash_short}`}
                >
                  Listeden çıkar
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}


